import React, { useState } from 'react';
import ExpandSection from 'src/components/ExpandSection';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import GenericTable from 'src/components/GenericTable';
import { TableIds } from 'src/api/types/misc';
import { NoData, TableSkeleton } from 'src/common';
import { useColumns } from './useColumns';
import {
  ConfirmDeleteModal,
  LinkControlsToScenarioModal,
  LinkIncidentsToScenarioModal,
  ManageRiskModal,
} from 'src/components/modals';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { AddRiskScenario, ScenarioTableExpandContent } from './comps';
import {
  AssessmentRiskRegisterQuery,
  useAssessmentRiskRegisterQuery,
  useDeleteScenarioMutation,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { AiSuggestControl } from '../../comps';
import useVersion from 'src/hooks/useVersion';

type AssessmentScenario = NonNullable<AssessmentRiskRegisterQuery['assessment']>['scenarios'][number];
interface RiskRegisterSectionProps {
  assessmentId: string;
  assessmentName: string;
  showAllProjected: boolean;
}

export const RiskRegisterSection: React.FC<RiskRegisterSectionProps> = ({
  assessmentId,
  assessmentName,
  showAllProjected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [deleteScenarioId, setDeleteScenarioId] = useState<string | undefined>(undefined);
  const [addIncidentScenario, setAddIncidentScenario] = useState<AssessmentScenario | undefined>(undefined);
  const [addControlScenario, setAddControlScenario] = useState<AssessmentScenario | undefined>(undefined);
  const [addStrategyScenario, setAddStrategyScenario] = useState<AssessmentScenario | undefined>(undefined);
  const { refetchQuery } = useCache();
  const { versionId } = useVersion();

  const { data: rrData, loading } = useAssessmentRiskRegisterQuery({
    variables: { id: assessmentId, versionId },
    skip: !hasOpened,
  });
  const isLoading = isOpen && loading && !rrData?.assessment;

  const scenarios = rrData?.assessment?.scenarios || [];

  const hasScenariosWithoutControls = scenarios.some((el) => !el.riskManagementPlans.length);

  const [remove] = useDeleteScenarioMutation({
    onCompleted: () => {
      mpEvent(MPEvents.ScenarioDelete, { id: deleteScenarioId as string, tags: ['SCENARIO'] });
    },
  });

  const { columns, GTColumnSelectAnchorExported } = useColumns({
    data: scenarios,
    assessmentId,
    version: versionId,
    setDeleteScenarioId,
    showAllProjected,
    onAddStrategy: setAddStrategyScenario,
    onAddControl: setAddControlScenario,
    onAddIncident: setAddIncidentScenario,
  });

  return (
    <>
      {addIncidentScenario && (
        <LinkIncidentsToScenarioModal
          onClose={() => setAddIncidentScenario(undefined)}
          invalidateQueries={['assessmentRiskRegister']}
          scenarioId={addIncidentScenario.id}
          subTitle={{
            title: 'SCENARIO',
            value: addIncidentScenario.name || '',
          }}
        />
      )}
      {addStrategyScenario && (
        <ManageRiskModal
          scenarioDetails={addStrategyScenario}
          onClose={() => setAddStrategyScenario(undefined)}
          invalidateQueries={['assessment']}
          subTitle={{
            title: 'SCENARIO',
            value: addStrategyScenario.name || '',
          }}
        />
      )}
      {addControlScenario && (
        <LinkControlsToScenarioModal
          onClose={() => {
            setAddControlScenario(undefined);
          }}
          invalidateQueries={['assessment']}
          scenarioId={addControlScenario.id}
          subTitle={{
            title: 'SCENARIO',
            value: addControlScenario.name || '',
          }}
        />
      )}
      {deleteScenarioId && (
        <ConfirmDeleteModal
          onConfirm={async () => {
            await remove({ variables: { id: deleteScenarioId } });
            queryCache.invalidateQueries([QKeys.Audit]);
            await refetchQuery({ include: ['assessment', 'assessmentRiskRegister'] });
            showSuccessToast(toastMessages.scenario.deleted);
            setDeleteScenarioId(undefined);
          }}
          onClose={() => setDeleteScenarioId(undefined)}
        />
      )}
      <ExpandSection
        title="RISK REGISTER"
        sectionId="assessment_risk-register"
        onChange={(open) => {
          setIsOpen(open);
          if (open) setHasOpened(true);
        }}
        endComponent={
          isOpen && !versionId ? (
            <AddRiskScenario
              assessmentId={assessmentId}
              assessmentName={assessmentName}
              existingScenarioIds={scenarios.map((s) => s.id)}
            />
          ) : undefined
        }
      >
        {isLoading ? (
          <TableSkeleton />
        ) : scenarios.length ? (
          <>
            {hasScenariosWithoutControls && (
              <AiSuggestControl
                assessment={{ id: assessmentId, name: assessmentName }}
                wrapperStyle={{ marginBottom: 12 }}
              />
            )}
            <GenericTable
              tableId={TableIds.assessmentScenarios}
              data={scenarios}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(scen) => {
                const hasControls = scen.riskManagementPlans?.length > 0;
                if (!hasControls) return null;
                return <ScenarioTableExpandContent scenarioId={scen.id} scenario={scen} assessmentId={assessmentId} />;
              }}
              hideExpandContentWhenClosed
              searchable={['name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at']}
              initialSort={[{ id: 'updated_at', desc: true }]}
            />
          </>
        ) : (
          <NoData title="No scenarios added" />
        )}
      </ExpandSection>
    </>
  );
};
